"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.informationResponseFunctionForQuestion = exports.informationResponseFunctionForAnswer = exports.informationCharacteristicCurve4pl = exports.negativeLogLikelihood = exports.logLikelihood = exports.gradedResponseModel2pl = exports.invLogisticIrt2pl = exports.ilogt = exports.erf = exports.getParamsFromConfig = void 0;
// take an exampleObject and for each variable, add a property called `params`
// which is an array of `params` which are triples of (alpha, beta1, beta2)
// The first element is (alpha, undefined, beta)
// the middle elements are (alpha, beta of the item before, beta)
// the final element is (alpha, beta of the item before, undefined)
const keyBy_1 = __importDefault(require("lodash/keyBy"));
/**
 * @typedef {import('./types').IRTConfig} IRTConfig
 */
/**
 * @typedef {import('./types').Response} Response
 */
/**
 * @typedef {import('./types').OptionalNumber} OptionalNumber
 */
/**
 * @typedef {import('./types').IRTParams} IRTParams
 */
/**
 * @param {{ beta: OptionalNumber }} x
 * @param {{ beta: OptionalNumber }} y
 * @returns {number}
 */
function compareCoefficients(x, y) {
    if (x.beta === null && y.beta === null) {
        return 0;
    }
    if (x.beta === null) {
        return -1;
    }
    if (y.beta === null) {
        return 1;
    }
    return x.beta - y.beta;
}
/**
 * @param {IRTConfig} config
 * @returns {IRTParams}
 */
function getParamsFromConfig(config) {
    /** @type {IRTParams} */
    const params = {};
    Object.keys(config).forEach((key) => {
        const variable = config[key];
        const { alpha, coefficients } = variable;
        const sorted = [...coefficients].sort(compareCoefficients);
        /** @type {IRTParams['string']} */
        const variableParams = {};
        /** @type {OptionalNumber} */
        let beta1;
        /** @type {OptionalNumber} */
        let beta2;
        let i = 0;
        let j = 1;
        while (j <= sorted.length - 1) {
            if (sorted[i].beta === sorted[j].beta) {
                j += 1;
            }
            else {
                const coefficient = sorted[i];
                const coefficientNext = sorted[j];
                beta1 = coefficient.beta;
                beta2 = coefficientNext.beta;
                for (let k = i; k < j; k += 1) {
                    variableParams[sorted[k].value] = [alpha, beta1, beta2];
                }
                i = j;
                j += 1;
            }
        }
        if (i <= sorted.length - 1 &&
            sorted[i].beta === sorted[sorted.length - 1].beta) {
            beta1 = sorted[sorted.length - 1].beta;
            for (let k = i; k <= sorted.length - 1; k += 1) {
                variableParams[sorted[k].value] = [alpha, beta1, null];
            }
        }
        // variableParams.push([alpha, beta1, undefined]);
        params[key] = variableParams;
    });
    return params;
}
exports.getParamsFromConfig = getParamsFromConfig;
/**
 * Taylor series approximation of the error function erf.
 * This is approximated by
 * erf(x) ~= 2/sqrt(pi) * Sigma_0^inf(((-1^n)(z^(2n+1) )/n!(2n+1)))
 * https://en.wikipedia.org/wiki/Error_function
 *
 * the values for approximation are
 * a=-1^n
 * b=2n+1
 * c=z^(b)
 * d=n!
 *
 * so that forms erf(x) = 2/sqrt(pi) * Sigma (ac/bd)
 *
 * where s is the intermediate sum
 *
 * @param {number} z - value to calculate erf for
 * @param {number} approxLevel - number of iterations to use in approximation (integer)
 * @returns number
 */
function erf(z, approxLevel = 15) {
    let a = 1;
    let b = 1;
    let d = 1;
    let s = z;
    for (let i = 1; i < approxLevel; i += 1) {
        a *= -1;
        b += 2;
        const c = z ** b;
        d *= i;
        s += (a * c) / (b * d);
    }
    return (2 / Math.sqrt(Math.PI)) * s;
}
exports.erf = erf;
/**
 *
 * The inverse logistic function
 *  e^x/(1+e^x)
 * @param {number} x
 * @returns number
 */
function ilogt(x) {
    return Math.exp(x) / (Math.exp(x) + 1);
}
exports.ilogt = ilogt;
/**
 *
 * @param {number} theta
 * @param {number} alpha
 * @param {number} beta
 * @returns number
 */
function invLogisticIrt2pl(theta, alpha, beta) {
    const exponent = alpha * (theta - beta);
    const probability = 1 / (1 + Math.exp(-exponent));
    return probability;
}
exports.invLogisticIrt2pl = invLogisticIrt2pl;
// I think this is an equivalent implementation, and has been tested, but the
// above is easier to read.
// export function invLogisticIrt2pl(theta, alpha, beta) {
//   const p = alpha * (theta - beta);
//   return ilogt(p);
// }
/**
 *
 * @param {number} theta
 * @param {number} alpha
 * @param {OptionalNumber} beta1
 * @param {OptionalNumber} beta2
 * @returns number
 */
function gradedResponseModel2pl(theta, alpha, beta1, beta2) {
    let result;
    // We will use `undefined` to refer to +/- infinity for the bounds
    // It is not possible to cover both sides of the interval, so it
    // can be one-sided open only.
    if (beta1 === null && typeof beta2 === 'number') {
        result = 1 - invLogisticIrt2pl(theta, alpha, beta2);
    }
    else if (beta2 === null && typeof beta1 === 'number') {
        result = invLogisticIrt2pl(theta, alpha, beta1);
    }
    else if (typeof beta1 === 'number' && typeof beta2 === 'number') {
        result =
            invLogisticIrt2pl(theta, alpha, beta1) -
                invLogisticIrt2pl(theta, alpha, beta2);
        // TODO - double check the IRT implications regarding non monotonicity
        if (result < 0) {
            // console.warn(
            //   'Your betas are not monotone increasing - please double check',
            // );
            result *= -1;
        }
    }
    else {
        // Throw an error
        throw new Error('Beta 1 or Beta 2 need to be numbers');
    }
    if (result < 0 || result > 1) {
        throw new Error('GRM should return a probability');
    }
    return result;
}
exports.gradedResponseModel2pl = gradedResponseModel2pl;
/**
 * Calculates the log likelihood of theta given the responses
 * and the calibrations
 * @param {number} theta
 * @param {Response[]} responses
 * @param {IRTParams} params
 */
function logLikelihood(theta, responses, params) {
    if (responses.length === 0) {
        // console.warn(
        //   `there are no responses for log likelihood - all results yield 0`,
        //   {
        //     code: 'no responses for log likelihood',
        //     detail: `responses are - ${responses} \n\n params are ${params}`,
        //   },
        // );
        return 0;
    }
    let ll = 0;
    responses.forEach((response) => {
        const correspondingParams = params[response.questionId] &&
            params[response.questionId][response.answer.value];
        if (!correspondingParams) {
            // throw new Error(
            //   `The parameters must exist for a given question. No parameters for questionId ${response.questionId}`,
            // );
            return;
        }
        const [alpha, beta1, beta2] = correspondingParams;
        // console.log(params[response.questionId]);
        // console.log(response);
        // console.log(correspondingParams);
        ll += Math.log(gradedResponseModel2pl(theta, alpha, beta1, beta2));
    });
    if (ll === 0 && responses.length > 0) {
        // console.log('throwing ll=0');
        // console.log(responses);
        // console.log(params);
        // console.warn(
        //   `Log likelihood is still 0 - would generally expect this to be >0`,
        //   {
        //     code: 'LL is zero',
        //     detail: `responses are - ${responses} \n\n params are ${params}`,
        //   },
        // );
    }
    return ll;
}
exports.logLikelihood = logLikelihood;
/**
 * Calculates the negative log likelihood of theta given the responses
 * This is useful for minimising functions - the minimum of the negative
 * log likelihood is the maximum of the log likelihood which is maximum likelihood
 * @param {number} theta
 * @param {Response[]} responses
 * @param {IRTParams} params
 * @returns number
 */
function negativeLogLikelihood(theta, responses, params) {
    return -logLikelihood(theta, responses, params);
}
exports.negativeLogLikelihood = negativeLogLikelihood;
/**
 * @param {number} theta
 * @param {number} a
 * @param {number} b
 * @param {number} c
 * @param {number} d
 * @returns {number}
 */
function informationCharacteristicCurve4pl(theta, a, b, c = 0, d = 1) {
    return c + (d - c) / (1 + Math.exp(-a * (theta - b)));
}
exports.informationCharacteristicCurve4pl = informationCharacteristicCurve4pl;
/**
 *
 * @param {number} theta - estimate of ability
 * @param {number} alpha - Difficulty of question
 * @param {number} beta - Difficulty level of response
 * @returns {number}
 */
function informationResponseFunctionForAnswer(theta, alpha, beta) {
    const p = invLogisticIrt2pl(theta, alpha, beta);
    const q = 1 - p;
    return alpha * alpha * p * q;
}
exports.informationResponseFunctionForAnswer = informationResponseFunctionForAnswer;
/**
 * Compute the amount of information given by a question for a given theta.
 * This is the sum of the information responses for each individual level
 * of the question . \Sigma_questions I(q|theta)
 * @param {number} theta - The theta for which you are calculating the information
 * @param {IRTParams['string']} questionParams - array of item parameters as triples of [alpha, beta_1, beta_2]
 * @returns {number} information
 */
function informationResponseFunctionForQuestion(theta, questionParams) {
    let information = 0;
    // NOTE: If value at index '1' is the same it means that
    //       corresponding responses had the same "beta" coefficient
    //       and we collapsed them to a single logical response.
    Object.values((0, keyBy_1.default)(questionParams, '1')).forEach((param) => {
        const [alpha, beta1] = param;
        // Getting the lowest level correct is uninformative as q(x|the) = 0
        // and so information must, by definition, be 0
        if (typeof beta1 === 'number') {
            information += informationResponseFunctionForAnswer(theta, alpha, beta1);
        }
    });
    return information;
}
exports.informationResponseFunctionForQuestion = informationResponseFunctionForQuestion;
//# sourceMappingURL=utilities.js.map